import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'ff/debrief/store';
import { findCurrentSegment } from 'ff/debrief/utils';
import { tracklogApi } from 'ff/debrief/features/TracklogApi';
import * as AppStore from 'ff/debrief/features/AppSlice';

type Timer = number;

export interface ChronoState {
    currentOffset: number;
    currentPoint: number[];
    tickerSpeed: number;
    ticker: Timer;
    lastTimestamp: number;
    playStartOffset: number;
    playStartTime: number;
}

const initialChronoState: ChronoState = {
    currentOffset: 0,
    currentPoint: null,
    ticker: null,
    tickerSpeed: 1,
    lastTimestamp: 0,
    playStartOffset: 0,
    playStartTime: 0,
};

export const chronoSlice = createSlice({
    name: 'ChronoSlice',
    initialState: initialChronoState,
    reducers: {
        setTicker: (state, action: PayloadAction<Timer>) => {
            state.ticker = action.payload;
        },
        setLastTimestamp: (state, action: PayloadAction<number>) => {
            state.lastTimestamp = action.payload;
        },
        setTickerSpeed: (state, action: PayloadAction<number>) => {
            state.tickerSpeed = action.payload;
        },
        pauseTicker: state => {
            if (state.ticker) {
                cancelAnimationFrame(state.ticker);
                state.ticker = null;
            }
        },
        skip: (state, action: PayloadAction<number>) => {
            state.currentOffset += action.payload;
        },
        setOffset: (state, action: PayloadAction<number>) => {
            state.currentOffset = action.payload;
        },
        setPlayStartOffset: (state, action: PayloadAction<number>) => {
            state.playStartOffset = action.payload;
        },
        setPlayStartTime: (state, action: PayloadAction<number>) => {
            state.playStartTime = action.payload;
        },
        reset: state => {
            if (state.ticker) {
                cancelAnimationFrame(state.ticker);
            }
            state.currentOffset = 0;
            state.ticker = null;
            state.currentPoint = null;
            state.lastTimestamp = 0;
        },
    },
});

export const actions = chronoSlice.actions;

export function playTicker(currentTracklogUUID: string): AppThunk {
    return (dispatch, getState) => {
        const isTicking = getState().chronoSlice.ticker;
        if (isTicking) return;
        const startTime = performance.now();
        const startOffset = getState().chronoSlice.currentOffset;
        dispatch(actions.setPlayStartTime(startTime));
        dispatch(actions.setPlayStartOffset(startOffset));
        dispatch(actions.setLastTimestamp(startTime));

        function animate(currentTime: number) {
            const state = getState().chronoSlice;
            const speed = state.tickerSpeed;

            // Calculate elapsed time since play started
            const elapsedTime = (currentTime - state.playStartTime) / 1000; // Convert to seconds

            // Calculate new offset based on elapsed time and speed
            const newOffset = state.playStartOffset + elapsedTime * speed;
            dispatch(actions.setOffset(newOffset));

            // Find and select current segment
            const cachedDebriefQuery =
                tracklogApi.endpoints.getDebriefByTrackUUID.select(
                    currentTracklogUUID
                )(getState());
            const currSeg = findCurrentSegment(
                cachedDebriefQuery.data?.debriefSegments,
                getState().chronoSlice.currentOffset
            );
            dispatch(AppStore.actions.setSelectedSegment(currSeg));

            dispatch(actions.setLastTimestamp(currentTime));

            // Continue animation if still ticking
            if (getState().chronoSlice.ticker) {
                const animationId = requestAnimationFrame(animate);
                dispatch(actions.setTicker(animationId));
            }
        }

        const animationId = requestAnimationFrame(animate);
        dispatch(actions.setTicker(animationId));
    };
}

export function setSpeed(speed: number): AppThunk {
    return (dispatch, getState) => {
        const state = getState().chronoSlice;
        // If playing, update the play start time and offset to prevent time jumps
        if (state.ticker) {
            const currentTime = performance.now();
            const currentOffset = state.currentOffset;
            dispatch(actions.setPlayStartTime(currentTime));
            dispatch(actions.setPlayStartOffset(currentOffset));
        }
        dispatch(actions.setTickerSpeed(speed));
    };
}
